import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Clock, AlertCircle, LogIn, LogOut, Coffee, ArrowLeft, MapPin, Plus } from 'lucide-react';
import { config } from '../../lib/config';
import { useGeolocation, formatAccuracy } from '../../hooks/useGeolocation';
import { KioskMissedPunchWizard } from './KioskMissedPunchWizard';
const TIME_API_URL = config.api.time;
/**
 * Kiosk PIN Pad Component - Device-based Authentication
 *
 * Two-screen flow:
 * 1. PIN entry screen
 * 2. Session status screen with punch actions
 */
export function KioskPinPad() {
    const [pin, setPin] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sessionStatus, setSessionStatus] = useState(null);
    const [lastPunch, setLastPunch] = useState(null);
    const [elapsedDisplay, setElapsedDisplay] = useState('');
    const [missedPunchWizardOpen, setMissedPunchWizardOpen] = useState(false);
    // Geolocation for punch location tracking
    const { location, error: locationError, isLoading: locationLoading, permissionState, requestLocation, } = useGeolocation();
    const kioskToken = localStorage.getItem('lalohr_kiosk_token');
    const kioskDevice = JSON.parse(localStorage.getItem('lalohr_kiosk_device') || '{}');
    // Request location permission on mount
    useEffect(() => {
        requestLocation();
    }, [requestLocation]);
    // Update elapsed time display every second when session is active
    useEffect(() => {
        if (!sessionStatus || sessionStatus.status === 'CLOCKED_OUT') {
            return;
        }
        const updateElapsed = () => {
            // For ON_BREAK: show break duration (time since break started)
            // For CLOCKED_IN: show total work time (time since clock-in minus completed breaks)
            const startTime = sessionStatus.status === 'ON_BREAK'
                ? sessionStatus.breakStartTime
                : sessionStatus.clockInTime;
            if (!startTime)
                return;
            const start = new Date(startTime).getTime();
            const now = new Date().getTime();
            let diff = now - start;
            // For CLOCKED_IN status, subtract completed break time
            if (sessionStatus.status === 'CLOCKED_IN' && sessionStatus.totalBreakSeconds) {
                diff -= sessionStatus.totalBreakSeconds * 1000;
            }
            // Ensure we don't show negative time
            diff = Math.max(0, diff);
            const hours = Math.floor(diff / 3600000);
            const minutes = Math.floor((diff % 3600000) / 60000);
            const seconds = Math.floor((diff % 60000) / 1000);
            setElapsedDisplay(`${hours}h ${minutes}m ${seconds}s`);
        };
        updateElapsed();
        const interval = setInterval(updateElapsed, 1000);
        return () => clearInterval(interval);
    }, [sessionStatus]);
    const handlePinChange = (value) => {
        const digits = value.replace(/\D/g, '').slice(0, 6);
        setPin(digits);
    };
    const handlePinSubmit = async () => {
        setError(null);
        if (pin.length !== 6) {
            setError('Please enter a 6-digit PIN');
            return;
        }
        if (!kioskToken) {
            setError('No kiosk device token found');
            return;
        }
        setIsLoading(true);
        try {
            // Verify PIN and get session status
            const response = await fetch(`${TIME_API_URL}/kiosk/session-status`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${kioskToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ pin }),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || error.error || 'Invalid PIN');
            }
            const data = await response.json();
            setSessionStatus(data);
        }
        catch (err) {
            setError(err.message || 'Failed to verify PIN');
        }
        finally {
            setIsLoading(false);
        }
    };
    const handlePunchAction = async (action) => {
        setError(null);
        setIsLoading(true);
        try {
            // Build request body with optional location
            const requestBody = {
                pin,
                action,
            };
            // Include location if available and permission granted
            if (location && permissionState === 'granted') {
                requestBody.location = {
                    latitude: location.latitude,
                    longitude: location.longitude,
                    accuracy: location.accuracy,
                    timestamp: location.timestamp,
                };
            }
            const response = await fetch(`${TIME_API_URL}/kiosk/punch`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${kioskToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || error.error || 'Failed to process punch');
            }
            const data = await response.json();
            setLastPunch({
                employeeName: data.employeeName,
                action: data.action,
                timestamp: data.timestamp,
            });
            // Clear and return to PIN screen after 3 seconds
            setTimeout(() => {
                setLastPunch(null);
                setSessionStatus(null);
                setPin('');
            }, 3000);
        }
        catch (err) {
            setError(err.message || 'Failed to process punch');
        }
        finally {
            setIsLoading(false);
        }
    };
    // Handler for missed punch submission from wizard
    const handleMissedPunchSubmit = async (data) => {
        const requestBody = {
            pin,
            action: data.action,
            timestamp: data.timestamp,
            isMissedPunch: true,
            missedPunchReason: data.missedPunchReason,
            requestedAt: data.requestedAt,
            // Include location if available
            ...(location && permissionState === 'granted' && {
                location: {
                    latitude: location.latitude,
                    longitude: location.longitude,
                    accuracy: location.accuracy,
                    timestamp: location.timestamp,
                },
            }),
        };
        const response = await fetch(`${TIME_API_URL}/kiosk/punch`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${kioskToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        });
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || error.error || 'Failed to submit missed punch');
        }
        const result = await response.json();
        // Show success and return to PIN screen
        setLastPunch({
            employeeName: result.employeeName,
            action: data.action,
            timestamp: result.timestamp,
        });
        setTimeout(() => {
            setLastPunch(null);
            setSessionStatus(null);
            setPin('');
        }, 3000);
    };
    const handleMissedPunchSuccess = () => {
        setMissedPunchWizardOpen(false);
    };
    const handleBackToPinEntry = () => {
        setSessionStatus(null);
        setPin('');
        setError(null);
    };
    const handleNumberClick = (num) => {
        if (pin.length < 6) {
            setPin(pin + num);
        }
    };
    const handleClear = () => {
        setPin('');
        setError(null);
    };
    const handleBackspace = () => {
        setPin(pin.slice(0, -1));
    };
    if (!kioskToken) {
        return (_jsxs(Card, { className: "w-full max-w-md mx-auto", children: [_jsx(CardHeader, { children: _jsxs(CardTitle, { className: "flex items-center gap-2", children: [_jsx(AlertCircle, { className: "h-5 w-5 text-red-600" }), "Device Not Paired"] }) }), _jsxs(CardContent, { children: [_jsxs("p", { className: "text-muted-foreground mb-4", children: ["This kiosk device has not been paired yet. Please navigate to", ' ', _jsx("code", { className: "bg-gray-100 px-2 py-1 rounded", children: "/kiosk/pair" }), " to complete pairing with a pairing code from your administrator."] }), _jsx(Button, { onClick: () => (window.location.href = '/kiosk/pair'), className: "w-full", children: "Go to Pairing Page" })] })] }));
    }
    // Success message screen
    if (lastPunch) {
        return (_jsx(Card, { className: "w-full max-w-md mx-auto border-green-500 bg-green-50", children: _jsx(CardContent, { className: "pt-6", children: _jsxs("div", { className: "text-center space-y-4", children: [_jsxs("div", { className: "text-6xl mb-4", children: [lastPunch.action === 'CLOCK_IN' && '👋', lastPunch.action === 'CLOCK_OUT' && '👋', (lastPunch.action === 'START_BREAK' || lastPunch.action === 'BREAK_START' || lastPunch.action === 'END_BREAK' || lastPunch.action === 'BREAK_END') && '☕'] }), _jsxs("h3", { className: "text-2xl font-semibold text-green-900", children: [lastPunch.action === 'CLOCK_IN' && `Welcome, ${lastPunch.employeeName}!`, lastPunch.action === 'CLOCK_OUT' && `Goodbye, ${lastPunch.employeeName}!`, (lastPunch.action === 'START_BREAK' || lastPunch.action === 'BREAK_START') && `Enjoy your break, ${lastPunch.employeeName}!`, (lastPunch.action === 'END_BREAK' || lastPunch.action === 'BREAK_END') && `Welcome back, ${lastPunch.employeeName}!`] }), _jsxs("p", { className: "text-lg text-green-700", children: [lastPunch.action === 'CLOCK_IN' && 'Clocked in', lastPunch.action === 'CLOCK_OUT' && 'Clocked out', (lastPunch.action === 'START_BREAK' || lastPunch.action === 'BREAK_START') && 'Break started', (lastPunch.action === 'END_BREAK' || lastPunch.action === 'BREAK_END') && 'Break ended', ' at ', new Date(lastPunch.timestamp).toLocaleTimeString()] })] }) }) }));
    }
    // Session status screen (after PIN verified)
    if (sessionStatus) {
        return (_jsx("div", { className: "space-y-6", children: _jsxs(Card, { className: "w-full max-w-md mx-auto", children: [_jsx(CardHeader, { children: _jsxs(CardTitle, { className: "flex items-center justify-between", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Clock, { className: "h-6 w-6" }), sessionStatus.employeeName] }), _jsxs(Button, { variant: "ghost", size: "sm", onClick: handleBackToPinEntry, className: "text-gray-600", children: [_jsx(ArrowLeft, { className: "h-4 w-4 mr-2" }), "Back"] })] }) }), _jsxs(CardContent, { className: "space-y-6", children: [error && (_jsx("div", { className: "p-3 rounded-md bg-red-50 border border-red-200", children: _jsx("p", { className: "text-sm text-red-800 text-center", children: error }) })), _jsxs("div", { className: "bg-blue-50 border border-blue-200 rounded-lg p-6", children: [_jsx("p", { className: "text-sm text-blue-700 text-center mb-2", children: "Current Status" }), _jsxs("p", { className: "text-2xl font-bold text-blue-900 text-center mb-4", children: [sessionStatus.status === 'CLOCKED_OUT' && 'Clocked Out', sessionStatus.status === 'CLOCKED_IN' && 'Clocked In', sessionStatus.status === 'ON_BREAK' && 'On Break'] }), sessionStatus.status !== 'CLOCKED_OUT' && (_jsxs("div", { className: "text-center", children: [_jsx("p", { className: "text-sm text-blue-700 mb-1", children: sessionStatus.status === 'ON_BREAK' ? 'Break Duration' : 'Time Worked' }), _jsx("p", { className: "text-3xl font-mono font-bold text-blue-900", children: elapsedDisplay })] })), sessionStatus.clockInTime && sessionStatus.status === 'CLOCKED_IN' && (_jsxs("p", { className: "text-xs text-blue-600 text-center mt-3", children: ["Clocked in at ", new Date(sessionStatus.clockInTime).toLocaleTimeString()] })), sessionStatus.breakStartTime && sessionStatus.status === 'ON_BREAK' && (_jsxs("p", { className: "text-xs text-blue-600 text-center mt-3", children: ["Break started at ", new Date(sessionStatus.breakStartTime).toLocaleTimeString()] }))] }), _jsxs("div", { className: "space-y-3", children: [sessionStatus.status === 'CLOCKED_OUT' && (_jsxs(Button, { onClick: () => handlePunchAction('CLOCK_IN'), disabled: isLoading, className: "w-full h-16 text-lg bg-green-600 hover:bg-green-700", size: "lg", children: [_jsx(LogIn, { className: "w-5 h-5 mr-2" }), isLoading ? 'Processing...' : 'Clock In'] })), sessionStatus.status === 'CLOCKED_IN' && (_jsxs(_Fragment, { children: [_jsxs(Button, { onClick: () => handlePunchAction('START_BREAK'), disabled: isLoading, className: "w-full h-16 text-lg bg-yellow-600 hover:bg-yellow-700", size: "lg", children: [_jsx(Coffee, { className: "w-5 h-5 mr-2" }), isLoading ? 'Processing...' : 'Start Break'] }), _jsxs(Button, { onClick: () => handlePunchAction('CLOCK_OUT'), disabled: isLoading, className: "w-full h-16 text-lg bg-red-600 hover:bg-red-700", size: "lg", children: [_jsx(LogOut, { className: "w-5 h-5 mr-2" }), isLoading ? 'Processing...' : 'Clock Out'] })] })), sessionStatus.status === 'ON_BREAK' && (_jsxs(Button, { onClick: () => handlePunchAction('END_BREAK'), disabled: isLoading, className: "w-full h-16 text-lg bg-blue-600 hover:bg-blue-700", size: "lg", children: [_jsx(LogIn, { className: "w-5 h-5 mr-2" }), isLoading ? 'Processing...' : 'End Break'] })), _jsxs(Button, { onClick: () => setMissedPunchWizardOpen(true), disabled: isLoading, variant: "outline", className: "w-full h-12 text-base border-orange-300 text-orange-700 hover:bg-orange-50", size: "lg", children: [_jsx(Plus, { className: "w-5 h-5 mr-2" }), "Add Missed Punch"] })] }), _jsx(KioskMissedPunchWizard, { open: missedPunchWizardOpen, onClose: () => setMissedPunchWizardOpen(false), onSuccess: handleMissedPunchSuccess, sessionStatus: { status: sessionStatus.status }, onSubmit: handleMissedPunchSubmit })] })] }) }));
    }
    // PIN entry screen
    return (_jsx("div", { className: "space-y-6", children: _jsxs(Card, { className: "w-full max-w-md mx-auto", children: [_jsxs(CardHeader, { children: [_jsxs(CardTitle, { className: "flex items-center gap-2 justify-center text-2xl", children: [_jsx(Clock, { className: "h-6 w-6" }), "Time Clock"] }), kioskDevice.name && (_jsx("p", { className: "text-sm text-center text-gray-600", children: kioskDevice.name }))] }), _jsxs(CardContent, { className: "space-y-6", children: [error && (_jsx("div", { className: "p-3 rounded-md bg-red-50 border border-red-200", children: _jsx("p", { className: "text-sm text-red-800 text-center", children: error }) })), _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "space-y-2", children: [_jsx("label", { className: "text-sm font-medium text-center block", children: "Enter Your 6-Digit PIN" }), _jsx(Input, { type: "password", value: pin, onChange: (e) => handlePinChange(e.target.value), placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022", className: "text-center text-2xl tracking-widest", maxLength: 6, autoFocus: true, onKeyPress: (e) => {
                                                if (e.key === 'Enter' && pin.length === 6) {
                                                    handlePinSubmit();
                                                }
                                            } }), _jsxs("p", { className: "text-xs text-muted-foreground text-center", children: [pin.length, "/6 digits"] })] }), _jsxs("div", { className: "grid grid-cols-3 gap-3", children: [['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((num) => (_jsx(Button, { onClick: () => handleNumberClick(num), variant: "outline", size: "lg", className: "text-2xl h-16", disabled: pin.length >= 6, children: num }, num))), _jsx(Button, { onClick: handleClear, variant: "outline", size: "lg", className: "h-16", children: "Clear" }), _jsx(Button, { onClick: () => handleNumberClick('0'), variant: "outline", size: "lg", className: "text-2xl h-16", disabled: pin.length >= 6, children: "0" }), _jsx(Button, { onClick: handleBackspace, variant: "outline", size: "lg", className: "h-16", children: "\u2190 Back" })] }), _jsx(Button, { onClick: handlePinSubmit, disabled: pin.length !== 6 || isLoading, className: "w-full h-14 text-lg", size: "lg", children: isLoading ? 'Verifying...' : 'Continue' }), _jsxs("div", { className: "flex items-center justify-center gap-2 text-xs text-muted-foreground pt-2", children: [_jsx(MapPin, { className: `h-4 w-4 ${permissionState === 'granted' && location
                                                ? 'text-green-600'
                                                : permissionState === 'denied'
                                                    ? 'text-red-500'
                                                    : 'text-gray-400'}` }), locationLoading ? (_jsx("span", { children: "Getting location..." })) : permissionState === 'granted' && location ? (_jsxs("span", { className: "text-green-600", children: ["Location tracking enabled (", formatAccuracy(location.accuracy), ")"] })) : permissionState === 'denied' ? (_jsx("span", { className: "text-red-500", children: "Location access denied" })) : permissionState === 'prompt' ? (_jsx("button", { onClick: requestLocation, className: "text-blue-600 hover:underline", children: "Enable location tracking" })) : locationError ? (_jsxs("span", { className: "text-amber-600", children: ["Location unavailable: ", locationError] })) : (_jsx("span", { children: "Location not available" }))] })] })] })] }) }));
}
